import { useState, useEffect } from 'react';
import './index.css';
import Keyboard from './components/Keyboard';
import ToolBar from './components/ToolBar';
import { getAvailablePlatforms, getLayout, getAvailableRegions } from './configs';
import ShortcutCapture from './components/ShortcutCapture';

// Add isModifierKey helper function
const isModifierKey = (code) => {
  return [
    'ShiftLeft', 'ShiftRight',
    'ControlLeft', 'ControlRight',
    'AltLeft', 'AltRight',
    'MetaLeft', 'MetaRight'
  ].includes(code);
};

function App() {
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const [platform, setPlatform] = useState('Mac');
  const [region, setRegion] = useState('GB');
  const [type, setType] = useState('QWERTY');
  const [activeModifiers, setActiveModifiers] = useState(new Set());
  const [savedShortcuts, setSavedShortcuts] = useState(() => {
    const saved = localStorage.getItem('shortcuts');
    return saved ? JSON.parse(saved) : [];
  });

  // Add handler functions
  const handlePlatformChange = (event) => {
    const newPlatform = event.target.value;
    setPlatform(newPlatform);
    // Reset region to first available option for new platform
    const availableRegions = getAvailableRegions(newPlatform);
    if (availableRegions.length > 0) {
      setRegion(availableRegions[0]);
    }
  };

  const handleRegionChange = (event) => {
    setRegion(event.target.value);
  };

  const handleTypeChange = (event) => {
    setType(event.target.value);
  };

  // Get current layout
  const currentLayout = getLayout(platform, region, type);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target.tagName === 'INPUT') return;
      event.preventDefault();

      const keyCode = event.code;
      setPressedKeys(prevKeys => new Set([...prevKeys, keyCode]));

      if (isModifierKey(keyCode)) {
        setActiveModifiers(prev => new Set([...prev, keyCode]));
      }
    };

    const handleKeyUp = (event) => {
      if (event.target.tagName === 'INPUT') return;

      const keyCode = event.code;

      setPressedKeys(prevKeys => {
        const newKeys = new Set(prevKeys);
        newKeys.delete(keyCode);
        return newKeys;
      });

      if (isModifierKey(keyCode)) {
        setActiveModifiers(prev => {
          const newActive = new Set(prev);
          newActive.delete(keyCode);
          // When a modifier key is released, clear all pressed keys
          // This helps with Mac Command key combinations
          setPressedKeys(new Set());
          return newActive;
        });
      }
    };

    // Add Command key specific handler
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setPressedKeys(new Set());
        setActiveModifiers(new Set());
      }
    };

    const handleBlur = () => {
      setPressedKeys(new Set());
      setActiveModifiers(new Set());
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div id="app">
      <header>
        <h1>Keyboard shortcut mapper</h1>
      </header>

      <ToolBar
        platform={platform}
        region={region}
        type={type}
        onPlatformChange={handlePlatformChange}
        onRegionChange={handleRegionChange}
        onTypeChange={handleTypeChange}
      />

      <Keyboard
        layout={currentLayout}
        pressedKeys={pressedKeys}
        activeModifiers={activeModifiers}
        savedShortcuts={savedShortcuts}
      />

      <ShortcutCapture
        pressedKeys={pressedKeys}
        activeModifiers={activeModifiers}
        platform={platform}
        region={region}
        layout={type}
        savedShortcuts={savedShortcuts}
        onShortcutsChange={setSavedShortcuts}
      />
    </div>
  );
}

export default App;
